import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

// Signature on-load moment: masked line-by-line reveal.
export const MaskedLines = ({
  lines,
  className = "",
  lineClass = "",
  delay = 0,
  stagger = 0.14,
  as: Tag = "h1",
}) => (
  <Tag className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
        <motion.span
          className={`block will-change-transform ${lineClass}`}
          initial={{ y: "112%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.1, ease: EASE, delay: delay + i * stagger }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export const Reveal = ({
  children,
  className = "",
  delay = 0,
  y = 30,
  amount = 0.15,
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount, margin: "-60px" }}
    transition={{ duration: 0.9, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

export const Stagger = ({ children, className = "", delay = 0 }) => (
  <motion.div
    className={className}
    initial="offscreen"
    whileInView="onscreen"
    viewport={{ once: true, amount: 0.1, margin: "-60px" }}
    variants={{
      offscreen: {},
      onscreen: {
        transition: { staggerChildren: 0.09, delayChildren: delay },
      },
    }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "" }) => (
  <motion.div
    className={className}
    variants={{
      offscreen: { opacity: 0, y: 30 },
      onscreen: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: EASE },
      },
    }}
  >
    {children}
  </motion.div>
);

// Subtle parallax depth for hero imagery.
export const useParallax = (distance = "12%") => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", distance]);
  return { ref, y };
};
